/**
 * @fileoverview RequiredLoader for loading global modules needed on every page.
 * 
 * This loader handles modules that are essential for the application's core functionality
 * and must be loaded on every page regardless of template, brand, or other conditions.
 * 
 * @author Roshan Jose
 * @version 2.0.0
 * @since 2025
 */

import { RouterHelper } from "../router-helper";
import { IModuleLoader, ModuleLoaderStatus } from "../interfaces/module-loader.interfaces";
import { getLocalization, getPageInfo } from "../../util/add-Page-Type-DataLayer";
import { gtmCustomEventLeadFlow } from "../../util/gtm-custom-event-lead-flow";
import { getBrandDetailsFromBrandJson } from "../../util/share";
import { initOnceWithDOMReady } from "../../util/dom-loader-check";
import { startLoader, stopLoader } from "../../util/loader";

/**
 * RequiredLoader manages global modules that are essential for every page.
 * 
 * This loader is responsible for loading core functionality modules that provide:
 * - Global utilities and helper functions
 * - Core application infrastructure
 * - Essential polyfills and compatibility layers
 * - Base styling and layout components
 * 
 * The RequiredLoader follows the singleton pattern and implements the IModuleLoader
 * interface for consistent behavior across the router system.
 * 
 * Features:
 * - Singleton pattern for global access
 * - Secure module loading via RouterHelper
 * - Comprehensive error handling and status tracking
 * - Timeout protection (30s default)
 * - Race condition prevention
 * 
 * @class RequiredLoader
 * @implements {IModuleLoader}
 */
export class RequiredLoader implements IModuleLoader {
    /** Singleton instance of the RequiredLoader */
    private static instance: RequiredLoader;

    /** RouterHelper instance for secure module loading operations */
    private readonly routerHelper: RouterHelper = RouterHelper.getInstance();

    /** Current status and state of the RequiredLoader */
    private readonly status: ModuleLoaderStatus = {
        isInitialized: false,
        loadedModules: [],
        failedModules: [],
        isLoading: false
    };

    /**
     * Gets the singleton instance of RequiredLoader.
     * Creates a new instance if one doesn't exist.
     * @returns The singleton RequiredLoader instance
     */
    static getInstance(): RequiredLoader {
        this.instance ??= new RequiredLoader();
        return this.instance;
    }

    /**
     * Initializes the RequiredLoader.
     * 
     * Sets up the loader for module loading operations and marks it as initialized.
     * This is a lightweight initialization as required modules don't need special setup.
     * 
     * @returns Promise that resolves when initialization is complete
     * @throws {Error} When initialization fails
     */
    async init(): Promise<void> {
        try {
            this.status.isInitialized = true;
        } catch (error) {
            this.status.lastError = error instanceof Error ? error.message : String(error);
            throw error;
        }
    }

    /**
     * Returns the human-readable name of this loader.
     * Used for logging, debugging, and status reporting.
     * @returns The loader's display name
     */
    getName(): string {
        return 'RequiredLoader';
    }

    /**
     * Returns a copy of the current loader status.
     * Provides real-time information about the loader's state.
     * @returns Current loader status information
     */
    getStatus(): ModuleLoaderStatus {
        return { ...this.status };
    }

    /**
     * Loads all required modules (delegates to requiredModules).
     * 
     * This is the main entry point for loading required modules,
     * implementing the IModuleLoader interface contract.
     * 
     * @returns Promise that resolves when all required modules are loaded
     */
    async loadModules(): Promise<void> {
        return this.requiredModules();
    }

    /**
     * Loads global modules that are required on every page.
     * 
     * Currently loads the global module bundle which contains:
     * - Core utilities and helper functions
     * - Application infrastructure components
     * - Essential polyfills and compatibility layers
     * 
     * Uses RouterHelper.loadModuleSafely for secure loading with timeout protection.
     * 
     * @returns Promise that resolves when global modules are loaded
     * @throws {Error} When module loading fails
     */
    async requiredModules(): Promise<void> {
        this.status.isLoading = true;

        try {

            const requiredModulesToLoad = [
                {
                    moduleName: "NeighborlyApp",
                    importFn: async () => {
                        const mod = await import(
                        /* webpackChunkName: "neighborly-app-module" */ "../../neighborly_app/neighborly-app"
                        );

                        // NeighborlyApp auto-instantiates via: export const neighborlyApp = new NeighborlyApp();

                        initOnceWithDOMReady('NeighborlyApp', () => {
                            // Check if the auto-instantiated version initialized properly
                            const sendNumberInput = document.getElementById('send-number');

                            if (sendNumberInput && mod?.neighborlyApp?.mobileNumberInput) {
                                // Auto-instantiation worked - DOM was ready during import
                            } else if (sendNumberInput && mod.NeighborlyApp) {
                                // DOM is ready now but auto-instantiation failed - create new instance
                                (globalThis as any).__NeighborlyAppInitialized = true;
                                (globalThis as any).__NeighborlyAppInstance = new mod.NeighborlyApp();
                                new mod.NeighborlyApp();
                            }
                        });

                        return mod;
                    },
                    instanceName: "NeighborlyApp",
                },

                {
                    moduleName: "SearchServiceInput",
                    importFn: async () => {
                        const mod = await import(
          /* webpackChunkName: "search-service-module" */ "../../search-service/search-service-input"
                        );
                        if (mod.SearchServiceInput && !(globalThis as any).__SearchServiceInputInitialized) {
                            // Use timing-safe pattern for SearchServiceInput initialization
                            initOnceWithDOMReady('SearchServiceInput', async () => {
                                if ((globalThis as any).__SearchServiceInputInitialized) return;
                                (globalThis as any).__SearchServiceInputInitialized = true;
                                // Create instance
                                const instance = await mod.SearchServiceInput.create();
                                (globalThis as any).__SearchServiceInputInstance = instance;
                            });
                        }
                        return mod;
                    },
                    instanceName: "SearchServiceInput",
                },
                {
                    moduleName: "MrElectricFormHandler",
                    importFn: async () => {
                        const mod = await import(
      /* webpackChunkName: "mrelectricform-loader" */ "../../find_service_near_me/findMyLocal"
                        );

                        // Only create instance if we're loading as chunk and DOMContentLoaded already fired
                        if (mod.MrElectricFormHandler) {
                            // If already initialized (DOM loader or chunk loader), do nothing
                            if ((globalThis as any).__MrElectricFormHandlerInitialized) {
                                return mod;
                            }

                            initOnceWithDOMReady("MrElectricFormHandler", () => {
                                if ((globalThis as any).__MrElectricFormHandlerInitialized) return;
                                (globalThis as any).__MrElectricFormHandlerInitialized = true;
                                mod.MrElectricFormHandler.getInstance();
                            });
                        }

                        return mod;
                    },
                    instanceName: "MrElectricFormHandler",
                },
                {
                    moduleName: "ServiceList",
                    importFn: async () => {
                        const mod = await import(
                        /* webpackChunkName: "service-list-module" */ "../../services/list"
                        );
                        if (mod.ServiceList) {
                            if (document.readyState !== 'loading') {
                                // DOM is already ready, original DOMContentLoaded won't fire
                                initOnceWithDOMReady('ServiceList', () => {
                                    const instance = new mod.ServiceList();
                                    (instance as any).initialize(); // call initialize manually
                                });
                            } 
                        }

                        return mod;
                    },
                    instanceName: "ServiceList",
                },
                {
                    moduleName: "MenuGenerator",
                    importFn: async () => {
                        const mod = await import(
                         /* webpackChunkName: "header-menu-config-loader" */ '../../header/headerMenuConfig'

                        );
                        if (mod.MenuGenerator) {
                            // Use timing-safe pattern for MenuGenerator initialization
                            initOnceWithDOMReady('MenuGenerator', () => {
                                if (!(globalThis as any).__MenuGeneratorInitialized) {
                                    (globalThis as any).__MenuGeneratorInitialized = true;
                                    new mod.MenuGenerator();
                                }
                            });
                        }
                        return mod;
                    },
                    instanceName: "MenuGenerator",
                },
                {
                    moduleName: "DisplayAddress",
                    importFn: async () => {
                        const mod = await import(
                        /* webpackChunkName: "display-address-loader" */ '../../contact_us/displayAddress'
                        );
                        if (mod.DisplayAddress) {
                            // Use timing-safe pattern for DisplayAddress initialization
                            initOnceWithDOMReady('DisplayAddress', async () => {
                                if (!(globalThis as any).__DisplayAddressInitialized) {
                                    (globalThis as any).__DisplayAddressInitialized = true;

                                    await mod.DisplayAddress.create();
                                }
                            });
                        }
                        return mod;
                    },
                    instanceName: "DisplayAddress",
                },
                {
                    moduleName: "StorageManager",
                    importFn: async () => {
                        const mod = await import(
                        /* webpackChunkName: "storage-manager-loader" */ '../../../global/storage-manager'
                        );
                        if (mod.StorageManager) {
                            // Use timing-safe pattern for StorageManager initialization (singleton)
                            initOnceWithDOMReady('StorageManager', () => {
                                if (!(globalThis as any).__StorageManagerInitialized) {
                                    (globalThis as any).__StorageManagerInitialized = true;
                                    mod.StorageManager.getInstance();
                                }
                            });
                        }
                        return mod;
                    },
                    instanceName: "StorageManager",
                },
                {
                    moduleName: "BrandLogo",
                    importFn: async () => {
                        const mod = await import(
                        /* webpackChunkName: "brand-logo-loader" */ '../../header/brandLogo'
                        );

                        if (mod.BrandLogo) {
                            if (document.readyState !== 'loading') {
                                // DOM is already ready, original auto-instantiation won't work properly
                                initOnceWithDOMReady('BrandLogo', () => {
                                    if (!(globalThis as any).__BrandLogoInitialized) {
                                        (globalThis as any).__BrandLogoInitialized = true;
                                        new mod.BrandLogo();
                                    }
                                });
                            }
                        }

                        return mod;
                    },
                    instanceName: "BrandLogo",
                },
                {
                    moduleName: "App",
                    importFn: async () => {
                        const mod = await import(
                        /* webpackChunkName: "location-map-app-loader" */ '../../location-search-map/App'
                        );
                        // Wrap in IIFE like in original App.ts
                        (async () => {
                            const conceptCode = (document.getElementById('conceptCode') as HTMLInputElement)?.value;
                            await getBrandDetailsFromBrandJson(conceptCode);

                            try {
                                const pageType = (document.getElementById('pageType') as HTMLInputElement)?.value;
                                if (pageType && pageType.toLocaleLowerCase() === "opus 2 lead flow confirmation page") {
                                    const franchiseWebLocationId = sessionStorage.getItem("RL_Weblocation_ID");
                                    const doingBusinessAs = sessionStorage.getItem("RL_DBAName");
                                    if (franchiseWebLocationId && doingBusinessAs) {
                                        gtmCustomEventLeadFlow({
                                            event: 'custEv_LocalDataLayer',
                                            locationID: franchiseWebLocationId,
                                            locationDBA: doingBusinessAs
                                        });
                                    }
                                }
                            } catch (error) {
                                this.routerHelper.routerConsoleLog(
                                    `gtmCustomEventLeadFlow - ${error}`,
                                    true,
                                    "RequiredLoader"
                                );
                            }

                            try {
                                const pageType = getPageInfo();
                                const pageLocalization = getLocalization();
                                gtmCustomEventLeadFlow({
                                    event: 'pageDataLayer',
                                    pageType,
                                    localization: pageLocalization
                                });
                            } catch (error) {
                                this.routerHelper.routerConsoleLog(
                                    `PagetypeDatalayer - ${error}`,
                                    true,
                                    "RequiredLoader"
                                );
                            }

                            const container = document.querySelector('.find-location-section');
                            if (container) {
                                startLoader();
                                const app = await mod.App.create();
                                window.addEventListener('resize', () => {
                                    app.windowResizeCallback();
                                });
                                stopLoader();
                            }
                        })();

                        return mod;
                    },
                    instanceName: "App",
                },
                {
                    moduleName: "PrequalReturnTracking",
                    importFn: async () => {
                        const mod = await import(
                        /* webpackChunkName: "prequal-return-tracking-loader" */ '../../financing/prequal-return-tracking'
                        );
                        if (mod.trackPrequalReturnToSite) {
                            // ADO 640622 workaround
                            initOnceWithDOMReady('PrequalReturnTracking', () => {
                                mod.trackPrequalReturnToSite();
                            });
                        }
                        return mod;
                    },
                    instanceName: "PrequalReturnTracking",
                }

            ];

            // load each required module
            for (const mod of requiredModulesToLoad) {
                await this.routerHelper.loadModuleSafely(
                    mod.moduleName,
                    mod.importFn,
                    mod.instanceName
                );

                this.status.loadedModules.push(mod.moduleName);
                this.routerHelper.routerConsoleLog(
                    `${mod.moduleName} loaded successfully`,
                    false,
                    "RequiredLoader"
                );
            }

        } catch (error) {
            this.status.failedModules.push('global');
            this.status.lastError = error instanceof Error ? error.message : String(error);
            throw error;
        } finally {
            this.status.isLoading = false;
        }
    }
}
